import { useState, useEffect, useContext } from "react";
import { supabase } from "../supabase";
import "react-phone-number-input/style.css";
import { Btn } from "../components/ui";
import { US_STATES } from "../utils/helpers";
import { composeName } from "../utils/names";
import { AuthContext, SiteSettingsContext } from "../contexts";
import { subscribeToOrderUpdates } from "../order-realtime";
import { insertAddressDeduped } from "../utils/addresses";
import { buildSelfServiceProfileUpdate } from "../api/profiles";
import AccountDropdown from "./account/AccountDropdown";
import ProfileSection from "./account/ProfileSection";
import OrdersSection from "./account/OrdersSection";
import CreditSection from "./account/CreditSection";
import TelegramSection from "./account/TelegramSection";
import AffiliateSection from "./account/AffiliateSection";


const AccountView = ({ setView, initialAccountTab, setInitialAccountTab }) => {
  const { user, setUser } = useContext(AuthContext);
  const [tab, setTab] = useState(() => {
    // Priority: explicit prop > sessionStorage > default
    if (initialAccountTab) return initialAccountTab;
    const saved = sessionStorage.getItem("accountTab");
    if (saved) return saved;
    return "profile";
  });

  // Clear initialAccountTab after consuming it
  useEffect(() => {
    if (initialAccountTab && setInitialAccountTab) setInitialAccountTab(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist tab selection to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("accountTab", tab);
  }, [tab]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderLabels, setSelectedOrderLabels] = useState([]);

  // All live labels on the order (original + reships) — RLS limits customers
  // to labels on their own orders.
  useEffect(() => {
    if (!selectedOrder?.id) { setSelectedOrderLabels([]); return; }
    supabase.from("shipping_labels")
      .select("id, tracking, carrier, service, kind, created_at")
      .eq("order_id", selectedOrder.id).is("voided_at", null)
      .order("created_at", { ascending: true })
      .then(({ data }) => setSelectedOrderLabels(data || []));
  }, [selectedOrder?.id]);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [paymentHandles, setPaymentHandles] = useState({});

  // Address book state
  const [addresses, setAddresses] = useState([]);
  const [, setAddrLoading] = useState(true);
  const [editingAddr, setEditingAddr] = useState(null);
  const [addrForm, setAddrForm] = useState({ label: "", name: "", first_name: "", middle_name: "", last_name: "", suffix: "", street: "", street2: "", city: "", state: "", zip: "" });
  const setAddrName = (patch) => setAddrForm(f => { const n = { ...f, ...patch }; return { ...n, name: composeName({ firstName: n.first_name, middleName: n.middle_name, lastName: n.last_name, suffix: n.suffix }) }; });

  // Profile edit state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ firstName: "", middleName: "", lastName: "" });
  const [profileSaved, setProfileSaved] = useState(false);
  const [addrError, setAddrError] = useState("");
  const [profileError, setProfileError] = useState("");

  // Load addresses from Supabase
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from("addresses")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at")
      .then(({ data }) => { if (data) setAddresses(data); setAddrLoading(false); })
      .catch(err => { console.error("Failed to load addresses:", err); setAddrLoading(false); });
  }, [user?.id]);

  // Load payment handles from site settings context
  const { settings: siteSettingsData } = useContext(SiteSettingsContext);
  useEffect(() => {
    if (siteSettingsData) setPaymentHandles({ venmo_handle: siteSettingsData.venmo_handle, cashapp_handle: siteSettingsData.cashapp_handle, zelle_handle: siteSettingsData.zelle_handle });
  }, [siteSettingsData]);

  // Load orders from Supabase
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from("orders")
      .select("*, order_items(*)")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => { if (data) setOrders(data); setOrdersLoading(false); })
      .catch(err => { console.error("Failed to load orders:", err); setOrdersLoading(false); });
  }, [user?.id]);

  // Real-time order status updates via Supabase Realtime
  useEffect(() => {
    if (!user?.id) return;
    return subscribeToOrderUpdates(user.id, (updated) => {
      setOrders(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o));
      if (selectedOrder?.id === updated.id) {
        setSelectedOrder(prev => ({ ...prev, ...updated }));
      }
    });
  }, [user?.id, selectedOrder?.id]);

  // Store Credit state
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditHistory, setCreditHistory] = useState([]);
  const [creditLoading, setCreditLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    supabase.rpc("get_store_credit_balance", { p_user_id: user.id })
      .then(({ data }) => { if (data != null) setCreditBalance(Number(data)); });
    supabase.from("store_credits").select("*").eq("user_id", user.id).order("created_at", { ascending: false })
      .then(({ data }) => { setCreditHistory(data || []); setCreditLoading(false); });
  }, [user?.id]);

  // Telegram linking state
  const [telegramLinked, setTelegramLinked] = useState(null); // null = loading, true/false
  const [telegramCode, setTelegramCode] = useState("");
  const [, setTelegramCodeExpiry] = useState(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramError, setTelegramError] = useState("");
  const [telegramSuccess, setTelegramSuccess] = useState("");

  // Load telegram link status
  useEffect(() => {
    if (!user?.id) return;
    supabase.from("profiles").select("telegram_chat_id").eq("id", user.id).maybeSingle()
      .then(({ data }) => setTelegramLinked(data?.telegram_chat_id ? true : false));
  }, [user?.id]);

  const generateTelegramCode = async () => {
    setTelegramLoading(true);
    setTelegramError("");
    setTelegramSuccess("");
    // Invalidate any existing unused codes
    await supabase.from("telegram_link_codes").update({ used: true }).eq("user_id", user.id).eq("used", false);
    // Generate 6-char alphanumeric code
    const code = Array.from(crypto.getRandomValues(new Uint8Array(4)))
      .map(b => "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"[b % 32]).join("");
    const expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString();
    const { error } = await supabase.from("telegram_link_codes").insert({
      user_id: user.id, code, expires_at: expiresAt, used: false,
    });
    setTelegramLoading(false);
    if (error) { setTelegramError("Failed to generate code. Try again."); return; }
    setTelegramCode(code);
    setTelegramCodeExpiry(expiresAt);
  };

  const unlinkTelegram = async () => {
    setTelegramLoading(true);
    setTelegramError("");
    const { error } = await supabase.from("profiles").update({ telegram_chat_id: null }).eq("id", user.id);
    setTelegramLoading(false);
    if (error) { setTelegramError("Failed to unlink. Try again."); return; }
    setTelegramLinked(false);
    setTelegramSuccess("Telegram account unlinked.");
    setTimeout(() => setTelegramSuccess(""), 3000);
  };

  // Affiliate state for customer account
  const [myAffiliate, setMyAffiliate] = useState(null);
  const [myAffiliateLoading, setMyAffiliateLoading] = useState(true);
  const [myCommissions, setMyCommissions] = useState([]);
  const [myApplication, setMyApplication] = useState(null);
  const [affiliateAppsEnabledCustomer, setAffiliateAppsEnabledCustomer] = useState(false);
  const [affiliateApplyForm, setAffiliateApplyForm] = useState({ code: "" });
  const [affiliateApplyError, setAffiliateApplyError] = useState("");
  const [affiliateApplying, setAffiliateApplying] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    supabase.from("affiliates").select("*").eq("user_id", user.id).maybeSingle()
      .then(({ data }) => {
        setMyAffiliate(data || null);
        if (data) {
          supabase.from("affiliate_commissions").select("*").eq("affiliate_id", data.id).order("created_at", { ascending: false })
            .then(({ data: comms }) => setMyCommissions(comms || []));
        }
        setMyAffiliateLoading(false);
      });
    supabase.from("affiliate_applications").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(1).maybeSingle()
      .then(({ data }) => setMyApplication(data || null));
    if (siteSettingsData) setAffiliateAppsEnabledCustomer(siteSettingsData.affiliate_applications_enabled ?? false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, siteSettingsData]);

  if (!user) return (
    <div style={{ maxWidth: 500, margin: "80px auto", padding: "0 24px", textAlign: "center" }} className="fade-up">
      <h2 style={{ fontSize: "28px", marginBottom: 12 }}>Sign in to manage your account</h2>
      <Btn onClick={() => setView("auth")}>Sign In</Btn>
    </div>
  );

  const openNewAddr = () => {
    setAddrError("");
    setAddrForm({ label: "", name: user?.name || "", first_name: "", middle_name: "", last_name: "", suffix: "", street: "", street2: "", city: "", state: "", zip: "" });
    setEditingAddr("new");
  };

  const openEditAddr = (addr) => {
    setAddrError("");
    setAddrForm({ label: addr.label, name: addr.name, first_name: addr.first_name || "", middle_name: addr.middle_name || "", last_name: addr.last_name || "", suffix: addr.suffix || "", street: addr.street, street2: addr.street2 || "", city: addr.city, state: addr.state, zip: addr.zip });
    setEditingAddr(addr);
  };

  const saveAddr = async () => {
    // Validate all required fields
    const { label, first_name, last_name, street, city, state, zip } = addrForm;
    if (!label.trim() || !first_name.trim() || !last_name.trim() || !street.trim() || !city.trim() || !state.trim() || !zip.trim()) {
      setAddrError("Label, first name, last name, and the full address are required.");
      return;
    }
    if (!US_STATES.some(s => s.code === state)) { setAddrError("Please select a state."); return; }
    if (!/^\d{5}$/.test(zip.trim())) { setAddrError("ZIP must be 5 digits."); return; }

    if (editingAddr === "new") {
      const isFirst = addresses.length === 0;
      const { data, error, duplicate } = await insertAddressDeduped({
        ...addrForm, user_id: user.id, is_default: isFirst,
      });
      if (error) { setAddrError("Failed to save address. Please try again."); return; }
      if (duplicate) { setAddrError("That address is already saved to your account."); return; }
      if (data) setAddresses(as => [...as, data]);
    } else {
      const { error } = await supabase.from("addresses").update(addrForm).eq("id", editingAddr.id);
      if (error) { setAddrError("Failed to update address. Please try again."); return; }
      setAddresses(as => as.map(a => a.id === editingAddr.id ? { ...a, ...addrForm } : a));
    }
    setAddrError("");
    setEditingAddr(null);
  };

  const deleteAddr = async (id) => {
    const addr = addresses.find(a => a.id === id);
    await supabase.from("addresses").delete().eq("id", id);
    const remaining = addresses.filter(a => a.id !== id);
    if (addr?.is_default && remaining.length > 0) {
      const next = remaining[0];
      await supabase.from("addresses").update({ is_default: true }).eq("id", next.id);
      setAddresses(remaining.map(a => a.id === next.id ? { ...a, is_default: true } : a));
    } else {
      setAddresses(remaining);
    }
  };

  const setDefault = async (id) => {
    // Clear all defaults, set new one
    await supabase.from("addresses").update({ is_default: false }).eq("user_id", user.id);
    await supabase.from("addresses").update({ is_default: true }).eq("id", id);
    setAddresses(as => as.map(a => ({ ...a, is_default: a.id === id })));
  };

  const saveProfile = async () => {
    setProfileError("");
    if (!profileForm.firstName.trim() || !profileForm.lastName.trim()) {
      setProfileError("First and last name are required.");
      return;
    }
    const profileUpdate = buildSelfServiceProfileUpdate(user, profileForm);

    // Use upsert to handle both existing and missing profile rows
    const { data: updated, error: updateError } = await supabase
      .from("profiles")
      .upsert(profileUpdate)
      .eq("id", user.id)
      .select()
      .single();

    if (updateError) {
      console.error("Profile update failed:", updateError);
      setProfileError("Failed to save: " + updateError.message);
      return;
    }
    console.log("Profile saved:", updated);
    setUser({ ...user, name: profileUpdate.name });
    setEditingProfile(false);
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 2500);
  };

  const accountTabs = [
    { id: "profile", label: "Profile & Addresses" },
    { id: "orders", label: "Order History" },
    { id: "credit", label: "Store Credit" },
    { id: "telegram", label: "Telegram" },
    ...((myAffiliate || affiliateAppsEnabledCustomer) ? [{ id: "affiliate", label: "Affiliate" }] : []),
  ];

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "48px 24px" }}>

      {/* Page header */}
      <div style={{ marginBottom: 32 }}>
        <p style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--amber)", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 6 }}>Account</p>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
          <h1 style={{ fontSize: "36px", lineHeight: 1 }}>{user.name}</h1>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: "13px", color: "var(--text-muted)" }}>{user.email}</span>
        </div>
      </div>

      {/* Tab selector dropdown */}
      <AccountDropdown tabs={accountTabs} tab={tab} setTab={setTab} />

      {/* ── PROFILE & ADDRESSES TAB ── */}
      {tab === "profile" && (
        <ProfileSection user={user} setUser={setUser} setView={setView} editingProfile={editingProfile} setEditingProfile={setEditingProfile} profileForm={profileForm} setProfileForm={setProfileForm} profileSaved={profileSaved} profileError={profileError} saveProfile={saveProfile} editingAddr={editingAddr} setEditingAddr={setEditingAddr} addrForm={addrForm} setAddrForm={setAddrForm} setAddrName={setAddrName} addrError={addrError} setAddrError={setAddrError} saveAddr={saveAddr} deleteAddr={deleteAddr} addresses={addresses} openNewAddr={openNewAddr} openEditAddr={openEditAddr} setDefault={setDefault} />
      )}
      {/* ── ORDER HISTORY TAB ── */}
      {tab === "orders" && (
        <OrdersSection selectedOrder={selectedOrder} setSelectedOrder={setSelectedOrder} selectedOrderLabels={selectedOrderLabels} paymentHandles={paymentHandles} ordersLoading={ordersLoading} orders={orders} setView={setView} />
      )}
      {/* ── STORE CREDIT TAB ── */}
      {tab === "credit" && (
        <CreditSection creditBalance={creditBalance} creditLoading={creditLoading} creditHistory={creditHistory} />
      )}
      {/* ── TELEGRAM TAB ── */}
      {tab === "telegram" && (
        <TelegramSection telegramLinked={telegramLinked} telegramLoading={telegramLoading} unlinkTelegram={unlinkTelegram} telegramCode={telegramCode} generateTelegramCode={generateTelegramCode} telegramError={telegramError} telegramSuccess={telegramSuccess} />
      )}
      {/* ── AFFILIATE TAB ── */}
      {tab === "affiliate" && (
        <AffiliateSection myAffiliateLoading={myAffiliateLoading} myAffiliate={myAffiliate} myCommissions={myCommissions} myApplication={myApplication} affiliateAppsEnabledCustomer={affiliateAppsEnabledCustomer} affiliateApplyForm={affiliateApplyForm} setAffiliateApplyForm={setAffiliateApplyForm} affiliateApplying={affiliateApplying} setAffiliateApplying={setAffiliateApplying} affiliateApplyError={affiliateApplyError} setAffiliateApplyError={setAffiliateApplyError} setMyApplication={setMyApplication} user={user} />
      )}
    </div>
  );
};


// ============================================================
// HOURS HELPERS (used by AdminView + ContactView)
// ============================================================

export default AccountView;
